import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, AlertTriangle, X } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';

function timeAgo(ms) {
    if (!ms) return '';
    const s = Math.floor((Date.now() - ms) / 1000);
    if (s < 60) return 'just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(ms).toLocaleDateString();
}

function typeOf(n) {
    const d = n.data || {};
    return String(d.type || d.notification_type || '').toUpperCase();
}

function isPaymentFailed(n) {
    return typeOf(n) === 'CUSTOMER_PAYMENT_FAILED' || /payment failed/i.test(n.title || '');
}

function isSlaAlert(n) {
    const t = typeOf(n);
    return (
        t === 'DISPATCH_SLA_BREACH' ||
        t === 'DISPATCH_ESCALATION' ||
        t === 'VENDOR_SUSPENDED' ||
        t === 'JOB_SLA_BREACH' ||
        t === 'VENDOR_WITHDREW' ||
        t === 'DISPUTE_RAISED' ||
        t === 'COMPLETION_OTP_ESCALATION' ||
        t === 'CUSTOMER_CANCELLATION_REJECTED' ||
        t === 'CUSTOMER_CANCELLATION_AUTO_SETTLED'
    );
}

function hrefFor(n) {
    const d = n.data || {};
    const sid = d.service_id;
    const cid = d.case_id;
    const t = typeOf(n);
    if (t === 'VENDOR_SUSPENDED') return '/vendor-performance';
    if (t === 'DISPATCH_SLA_BREACH' || t === 'DISPATCH_ESCALATION') {
        if (sid && cid) return `/add-service?case_id=${cid}&service_id=${sid}`;
        if (sid) return `/dispatch-queue`;
        return '/dispatch-queue';
    }
    if (t === 'VENDOR_WITHDREW') {
        if (sid && cid) return `/add-service?case_id=${cid}&service_id=${sid}`;
        if (sid) return '/services';
        return '/services';
    }
    if (t === 'DISPUTE_RAISED') return '/disputes';
    if (t === 'CUSTOMER_CANCELLATION_REJECTED' || t === 'CUSTOMER_CANCELLATION_AUTO_SETTLED') {
        if (sid && cid) return `/add-service?case_id=${cid}&service_id=${sid}`;
        return '/services';
    }
    if (sid && cid) return `/add-service?case_id=${cid}&service_id=${sid}`;
    if (sid) return '/services';
    return '/services';
}

function iconFor(n) {
    if (isPaymentFailed(n) || isSlaAlert(n)) return AlertTriangle;
    return Bell;
}

const NotificationCenter = () => {
    const navigate = useNavigate();
    const { notifications, unreadCount, loading, markRead, markAllRead, dismiss, clearAll } = useNotifications();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const openItem = (n) => {
        markRead(n.id);
        setOpen(false);
        navigate(hrefFor(n));
    };

    return (
        <div className="relative" ref={rootRef}>
            <button
                type="button"
                className="relative p-2 rounded-full hover:bg-gray-100 text-gray-600"
                onClick={() => setOpen((v) => !v)}
                aria-label="Notifications"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[10px] leading-4 text-center">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 mt-2 w-80 max-w-[92vw] bg-white border rounded-lg shadow-xl z-50 flex flex-col">
                    <div className="flex items-center justify-between px-3 py-2 border-b">
                        <span className="text-sm font-semibold">Notifications</span>
                        <div className="flex items-center gap-2 text-xs">
                            <button
                                type="button"
                                className="text-blue-600 disabled:text-gray-300"
                                onClick={markAllRead}
                                disabled={!unreadCount}
                            >
                                Mark all read
                            </button>
                            <button
                                type="button"
                                className="text-gray-500 disabled:text-gray-300"
                                onClick={clearAll}
                                disabled={!notifications.length}
                            >
                                Clear all
                            </button>
                        </div>
                    </div>

                    <div className="max-h-96 overflow-auto">
                        {loading && <p className="px-3 py-6 text-center text-sm text-gray-400">Loading…</p>}
                        {!loading && !notifications.length && (
                            <p className="px-3 py-6 text-center text-sm text-gray-400">No notifications</p>
                        )}
                        {notifications.map((n) => {
                            const Icon = iconFor(n);
                            return (
                                <div
                                    key={n.id}
                                    className={`flex gap-2 px-3 py-2 border-b last:border-b-0 ${n.read ? 'bg-white' : 'bg-blue-50'}`}
                                >
                                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${isPaymentFailed(n) || isSlaAlert(n) ? 'text-red-500' : 'text-gray-500'}`} />
                                    <button type="button" className="flex-1 text-left" onClick={() => openItem(n)}>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900 truncate">{n.title}</span>
                                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />}
                                        </div>
                                        {n.body && <p className="text-xs text-gray-600 line-clamp-2">{n.body}</p>}
                                        <span className="text-[11px] text-gray-400">{timeAgo(n.createdAt)}</span>
                                    </button>
                                    <button
                                        type="button"
                                        className="text-gray-300 hover:text-gray-500 shrink-0"
                                        onClick={() => dismiss(n.id)}
                                        aria-label="Dismiss"
                                    >
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
